import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { BeneficiaryConfig, BeneficiarySectionCode, HeartbeatStatus, InstanceSettings, InstanceSettingsUpdate, StrategyConfig, StrategyLevel } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, Field, PageHeader, SearchableSelect } from '../../components/ui';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { canAction, refresh } = useAuth();
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<InstanceSettings>('/api/settings') });
  const [form, setForm] = useState<InstanceSettingsUpdate>({});
  const [aiKey, setAiKey] = useState('');
  const [modules, setModules] = useState<string[]>([]);
  const [modulesDirty, setModulesDirty] = useState(false);
  const editable = canAction('settings.manage');

  useEffect(() => {
    if (!settings.data) return;
    const s = settings.data;
    setForm({ display_name_en: s.display_name_en, display_name_ar: s.display_name_ar, primary_color: s.primary_color, default_locale: s.default_locale, timezone: s.timezone, date_system: s.date_system, ai_provider: s.ai_provider });
    setModules(s.modules.filter((m) => m.enabled && !m.always_on).map((m) => m.code));
    setModulesDirty(false);
  }, [settings.data]);

  const saveModules = useMutation({
    mutationFn: () => api.put<InstanceSettings>('/api/settings/modules', { enabled_modules: modules }),
    onSuccess: async () => {
      toast.success(t('settings.modulesSaved'));
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await qc.invalidateQueries({ queryKey: ['parameter-categories'] });
      await refresh();
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const toggleModule = (code: string) => {
    setModules((m) => (m.includes(code) ? m.filter((c) => c !== code) : [...m, code]));
    setModulesDirty(true);
  };

  const save = useMutation({
    mutationFn: () => api.patch<InstanceSettings>('/api/settings', { ...form, ...(aiKey ? { ai_api_key: aiKey } : {}) }),
    onSuccess: async () => {
      toast.success(t('settings.saved'));
      setAiKey('');
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await refresh();
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const upload = useMutation({
    mutationFn: ({ file, variant }: { file: File; variant: 'main' | 'small' }) => api.upload<InstanceSettings>(`/api/settings/logo?variant=${variant}`, file),
    onSuccess: async () => {
      toast.success(t('settings.logoUploaded'));
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await refresh();
    },
    onError: (e) => toast.error(describeError(e, t)),
  });

  if (!settings.data) return null;
  const s = settings.data;

  return (
    <div className="max-w-3xl">
      <PageHeader title={t('settings.title')} actions={editable && <button type="button" className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>{t('app.save')}</button>} />
      <div className="grid gap-4">
        <section className="card p-4">
          <h2 className="mb-3 font-semibold">{t('settings.identity')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('settings.nameEn')} required><input className="input" disabled={!editable} value={form.display_name_en ?? ''} onChange={(e) => setForm({ ...form, display_name_en: e.target.value })} /></Field>
            <Field label={t('settings.nameAr')} required><input className="input" dir="rtl" disabled={!editable} value={form.display_name_ar ?? ''} onChange={(e) => setForm({ ...form, display_name_ar: e.target.value })} /></Field>
            <Field label={t('settings.primaryColor')}><input className="input h-10 w-24 p-1" type="color" disabled={!editable} value={form.primary_color ?? '#2E7D32'} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} /></Field>
            <Field label={t('settings.defaultLocale')}><SearchableSelect disabled={!editable} value={form.default_locale ?? 'en'} onChange={(v) => setForm({ ...form, default_locale: v as 'en' | 'ar' })} options={[{ value: 'en', label: 'English' }, { value: 'ar', label: 'العربية' }]} /></Field>
            <Field label={t('settings.timezone')}><input className="input" disabled={!editable} value={form.timezone ?? ''} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
            <Field label={t('settings.dateSystem')}><SearchableSelect disabled={!editable} value={form.date_system ?? 'gregorian'} onChange={(v) => setForm({ ...form, date_system: v as 'gregorian' | 'hijri_display' })} options={[{ value: 'gregorian', label: t('settings.gregorian') }, { value: 'hijri_display', label: t('settings.hijriDisplay') }]} /></Field>
          </div>
        </section>
        <section className="card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold">{t('settings.modules')}</h2>
            {editable && modulesDirty && <button type="button" className="btn-primary" disabled={saveModules.isPending} onClick={() => saveModules.mutate()}>{t('settings.applyModules')}</button>}
          </div>
          <p className="mb-3 text-xs text-neutral-500">{t('settings.modulesHint')}</p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {s.modules.map((m) => {
              const checked = m.always_on || modules.includes(m.code);
              const blocked = !m.available || m.depends_on.some((d) => !modules.includes(d) && !s.modules.find((x) => x.code === d)?.always_on);
              return (
                <li key={m.code} className="flex items-start gap-2 rounded-lg border border-neutral-200 p-2 text-sm dark:border-neutral-800">
                  <input type="checkbox" className="mt-1" checked={checked} disabled={!editable || m.always_on || (!checked && blocked)} onChange={() => toggleModule(m.code)} />
                  <div>
                    <div className="font-medium" dir="auto">{pick(i18n.language, m.name_en, m.name_ar)} <code className="text-xs text-neutral-400">{m.code}</code></div>
                    <div className="flex flex-wrap gap-1 text-xs text-neutral-500">
                      {m.always_on && <Badge tone="info">{t('settings.alwaysOn')}</Badge>}
                      {!m.available && <Badge tone="warn">{t('settings.notAvailable')}</Badge>}
                      {m.depends_on.length > 0 && <span>{t('settings.requires', { modules: m.depends_on.join(', ') })}</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
        {s.modules.some((m) => m.code === 'strategy' && m.enabled) && <StrategyLevelsSection editable={editable} />}
        {s.modules.some((m) => m.code === 'beneficiaries' && m.enabled) && <BeneficiarySectionsSection editable={editable} />}
        <section className="card p-4">
          <h2 className="mb-3 font-semibold">{t('settings.branding')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {(['main', 'small'] as const).map((variant) => {
              const url = variant === 'main' ? s.logo_url : s.logo_small_url;
              return (
                <div key={variant}>
                  <div className="label">{variant === 'main' ? t('settings.logo') : t('settings.logoSmall')}</div>
                  <div className="mb-2 flex h-20 w-40 items-center justify-center overflow-hidden rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700">
                    {url ? <img src={`${url}?v=${Date.now()}`} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-neutral-400">—</span>}
                  </div>
                  {editable && <input type="file" accept="image/png,image/svg+xml,image/webp,image/jpeg" className="text-xs" onChange={(e) => e.target.files?.[0] && upload.mutate({ file: e.target.files[0], variant })} />}
                </div>
              );
            })}
          </div>
        </section>
        <section className="card p-4">
          <h2 className="mb-3 font-semibold">{t('settings.ai')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('settings.aiProvider')}><SearchableSelect disabled={!editable} value={form.ai_provider ?? 'none'} onChange={(v) => setForm({ ...form, ai_provider: v as 'none' | 'gemini' | 'anthropic' })} options={[{ value: 'none', label: t('settings.aiNone') }, { value: 'gemini', label: 'Google Gemini' }, { value: 'anthropic', label: 'Anthropic Claude' }]} /></Field>
            <Field label={t('settings.aiKey')} hint={s.ai_api_key_set ? t('settings.aiKeySet') : undefined}><input className="input" type="password" disabled={!editable} value={aiKey} onChange={(e) => setAiKey(e.target.value)} autoComplete="off" /></Field>
          </div>
        </section>
        <CentralLink editable={editable} />
      </div>
    </div>
  );
}

/** An entity's link to central: is it configured, did the last report get through, what does central want it to run. */
function CentralLink({ editable }: { editable: boolean }) {
  const { t, i18n } = useTranslation();
  const { config } = useAuth();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['settings', 'heartbeat'], queryFn: () => api.get<HeartbeatStatus>('/api/settings/heartbeat'), enabled: config?.instance_kind === 'entity' });
  const send = useMutation({
    mutationFn: () => api.post<HeartbeatStatus>('/api/settings/heartbeat'),
    onSuccess: (res) => {
      qc.setQueryData(['settings', 'heartbeat'], res);
      if (res.last_ok) toast.success(t('settings.central.sent'));
      else toast.error(res.last_error ?? t('settings.central.failed'));
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  if (config?.instance_kind !== 'entity' || !status.data) return null;
  const s = status.data;
  return (
    <section className="card p-4">
      <h2 className="mb-1 font-semibold">{t('settings.central.title')}</h2>
      <p className="mb-3 text-xs text-neutral-500">{t('settings.central.hint')}</p>
      {!s.configured ? (
        <p className="text-sm text-neutral-500">{t('settings.central.notConfigured')}</p>
      ) : (
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div><div className="label">{t('settings.central.url')}</div><code dir="ltr">{s.central_url}</code></div>
          <div><div className="label">{t('settings.central.last')}</div>{s.last_sent_at ? <span className={s.last_ok ? 'text-brand-700' : 'text-red-600'}>{new Date(s.last_sent_at).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })} · {s.last_ok ? t('settings.central.ok') : (s.last_error ?? t('settings.central.failed'))}</span> : t('settings.central.never')}</div>
          <div><div className="label">{t('settings.central.desiredVersion')}</div><span dir="ltr">{s.desired_version ?? '—'}</span> <span className="text-xs text-neutral-500">({t('settings.central.running', { v: config.version })})</span></div>
          <div><div className="label">{t('settings.central.desiredModules')}</div><span dir="ltr">{s.desired_modules.join(', ') || '—'}</span></div>
          {editable && <div className="sm:col-span-2"><button type="button" className="btn-secondary" disabled={send.isPending} onClick={() => send.mutate()}>{t('settings.central.sendNow')}</button></div>}
        </div>
      )}
    </section>
  );
}

/** Which of the six cascade levels this entity uses (phase-1-scope section 1.3: configurable depth). */
function StrategyLevelsSection({ editable }: { editable: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ['strategy', 'config'], queryFn: () => api.get<StrategyConfig>('/api/strategy/config') });
  const [enabled, setEnabled] = useState<StrategyLevel[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (config.data) {
      setEnabled(config.data.enabled);
      setDirty(false);
    }
  }, [config.data]);
  const save = useMutation({
    mutationFn: () => api.put<StrategyConfig>('/api/settings/strategy', { enabled }),
    onSuccess: async () => {
      toast.success(t('settings.levelsSaved'));
      await qc.invalidateQueries({ queryKey: ['strategy'] });
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  if (!config.data) return null;
  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">{t('settings.strategyLevels')}</h2>
        {editable && dirty && <button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{t('settings.applyLevels')}</button>}
      </div>
      <p className="mb-3 text-xs text-neutral-500">{t('settings.strategyLevelsHint')}</p>
      <ol className="flex flex-wrap gap-2">
        {config.data.levels.map((l) => {
          const on = enabled.includes(l.code);
          return (
            <li key={l.code} className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm dark:border-neutral-800">
              <input type="checkbox" checked={on} disabled={!editable || l.code === 'strategy'} onChange={(e) => { setEnabled(e.target.checked ? [...enabled, l.code] : enabled.filter((c) => c !== l.code)); setDirty(true); }} />
              <span dir="auto">{pick(i18n.language, l.name_en, l.name_ar)}</span>
              <code className="text-xs text-neutral-400">{l.prefix}</code>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** Which personal-data sections individual beneficiaries carry in this entity (phase-1-scope §1.5). */
function BeneficiarySectionsSection({ editable }: { editable: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ['beneficiaries', 'config'], queryFn: () => api.get<BeneficiaryConfig>('/api/beneficiaries/config') });
  const [enabled, setEnabled] = useState<BeneficiarySectionCode[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (config.data) {
      setEnabled(config.data.enabled);
      setDirty(false);
    }
  }, [config.data]);
  const save = useMutation({
    mutationFn: () => api.put<BeneficiaryConfig>('/api/settings/beneficiaries', { enabled }),
    onSuccess: async () => {
      toast.success(t('settings.sectionsSaved'));
      await qc.invalidateQueries({ queryKey: ['beneficiaries'] });
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  if (!config.data) return null;
  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">{t('settings.beneficiarySections')}</h2>
        {editable && dirty && <button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{t('settings.applySections')}</button>}
      </div>
      <p className="mb-3 text-xs text-neutral-500">{t('settings.beneficiarySectionsHint')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {config.data.sections.map((sec) => (
          <label key={sec.code} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled.includes(sec.code)} disabled={!editable} onChange={(e) => { setEnabled(e.target.checked ? [...enabled, sec.code] : enabled.filter((c) => c !== sec.code)); setDirty(true); }} />
            <span dir="auto">{pick(i18n.language, sec.name_en, sec.name_ar)}</span>
          </label>
        ))}
      </div>
    </section>
  );
}
